"use client";

import { AuctionItem } from "@/types/auction";
import FavoriteButton from "../FavoriteButton";

import BidCountDown from "../BidCountDown";
import CurrentBid from "../CurrentBid";
import Button from "@/components/Elements/Button";
import { getAuctionItemLink } from "@/lib/links";
import Link from "next/link";
import classNames from "classnames";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import VideoWrapper from "@/components/VideoWrapper";
import NoImageFound from "@/components/NoImageFound";
import {
  BID_TIME_REMAINING_COUNTDOWN,
} from "@/lib/auctions";
import { addWidthParam, addArchivedClass } from "@/lib/utils";
import useConfirmedEnded from "@/hooks/useConfirmedEnded";
import useUserStore from "@/stores/useUserStore";
import Image from "next/image";
import { useShallow } from "zustand/react/shallow";
import useAuctionItemFavorite from "@/hooks/useAuctionItemFavorite";
import { useSearchParams } from "next/navigation";
import ArrowButton from "@/components/Elements/ArrowButton";
import { formatPrice, parseDate, parseToDate } from "@/lib/utils";
import useIsVisible from "@/hooks/useVisible";
import PopUpSvg from "@/icons/popup.svg";
import "swiper/css";
import "swiper/css/pagination";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import useModalStore from "@/hooks/useModalStore";
import { MarkerSvg } from "@/lib/temp";
import ModalElement from "@/components/Modal/Modal";
import AuctionBidField from "../../AuctionBidField";
import AuctionBidHistory from "../../AuctionBidHistory";
import AuctionEndedImage from "../../AuctionEndedImage";
import { FiCamera, FiVideo } from "react-icons/fi";

interface AuctionCallCardProps {
  item: AuctionItem;
  prefetchedBidsData?: any;
  fetchBids?: boolean;
  isFetching?: boolean;
  user?: any;
  totalItems?: number | null;
  serverTime: any;
  isLoading?: boolean;
}

const FavoriteButtonMemomized = React.memo(
  ({
    auctionItemFavorite,
    extraClassName,
  }: {
    auctionItemFavorite: any;
    extraClassName?: string;
  }) => (
    <FavoriteButton
      auctionItemFavorite={auctionItemFavorite}
      extraClassName={extraClassName}
    />
  )
);

const AuctionBidFieldMemomized = React.memo(
  ({
    hasEnded,
    leadingAutoBid,
    auctionItem,
    leadingBid,
    isLeadingAutoBid,
    ref,
  }: {
    hasEnded: boolean;
    auctionItem: AuctionItem;
    leadingBid: number;
    leadingAutoBid: any;
    isLeadingAutoBid: boolean;
    ref: any;
  }) => (
    <AuctionBidField
      hasEnded={hasEnded}
      leadingAutoBid={leadingAutoBid}
      auctionItem={auctionItem}
      leadingBid={leadingBid}
      ref={ref}
      isLeadingAutoBid={isLeadingAutoBid}
    />
  )
);

AuctionBidFieldMemomized.displayName = "AuctionBidFieldMemomized";
FavoriteButtonMemomized.displayName = "FavoriteButtonMemomized";

const GridViewItem: React.FC<AuctionCallCardProps> = ({
  item,
  prefetchedBidsData,
  serverTime,
  isLoading,
  isFetching = false,
}) => {
  const itemId = Number(item.itemId);
  const searchParams = useSearchParams();
  const linkRef = searchParams?.get("sida") || "1";
  const cardRef = useRef<HTMLDivElement>(null);
  const bidDivRef = useRef<HTMLDivElement>(null);
  const inputBidFieldRef = useRef<HTMLDivElement>(null);
  const { loginModal } = useModalStore();
  const [bidModal, setBidModal] = useState({
    isOpen: false,
    content: "",
  });
  const [toggleMobileDiv, setToggleMobileDiv] = useState(false);
  const [openLightbox, setOpenLightbox] = useState(false);
  const { user, userInfo } = useUserStore(useShallow((state) => state));
  const [showAllBids, setSHowAllBids] = useState(false);
  const itemLink = item && getAuctionItemLink(item, linkRef);
  const hasEnded = useConfirmedEnded(item?.itemEndTime, serverTime);

  const cardId = `auction-item-card-${itemId}`;

  const itemNameFormatted = `${
    item.itemSortNo ? `Rop ${item.itemSortNo}: ` : " "
  } ${item.itemTitle}`;

  const auctionItemFavorite = useAuctionItemFavorite({
    itemId: itemId,
    userId: user?.id,
  });

  const calculateTimeRemaining = useCallback(() => {
    const endTimeStr = parseDate(item.itemEndTime);
    const serverDateObject = parseToDate(serverTime);
    const now = serverDateObject.getTime();

    return endTimeStr - now;
  }, [item.itemEndTime, serverTime]);

  // If the time remaining is less than 2 minutes, we consider the auction to be ending
  const isEnding = calculateTimeRemaining() <= BID_TIME_REMAINING_COUNTDOWN;

  const formatDate = (date: string) => {
    const d = parseToDate(date);

    if (isNaN(d.getTime())) {
      return "Datum ej specificerat";
    }

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const bidData = prefetchedBidsData;

  const leadingAutoBid = bidData?.leadingAutoBid;

  const isLeadingAutoBid =
    leadingAutoBid && Number(leadingAutoBid?.autoUserId) === Number(user?.id);

  const isWinner =
    user && bidData ? item?.itemWinningUserId === user?.id : false;

  const leadingBid = bidData?.bids
    ? bidData?.bids[0]?.historyBidValue
    : item.itemMinBid;

  const containerClass = classNames(
    "relative bg-light-grey rounded-lg overflow-hidden flex flex-col items-start justify-start box-border transition-shadow duration-300 ease-in-out"
  );

  // Use media array if available (new format with mixed sorting)
  // Otherwise fall back to separate images array (old format)
  const hasMedia = item.media && item.media.length > 0;

  const images = hasMedia && item.media
    ? item.media.filter(m => m.type === 'image').map(m => m.url)
    : (item?.images ? Object.values(item.images) : []);

  const firstMediaItem = hasMedia && item.media ? item.media[0] : null;

  // Count photos and videos
  const photosCount = hasMedia && item.media ? item.media.filter(m => m.type === 'image').length : images.length;
  const videosCount = hasMedia && item.media ? item.media.filter(m => m.type === 'video').length : 0;

  // Create slides for lightbox from media array
  const largeImages = useMemo(() => {
    if (hasMedia && item.media) {
      return item.media.map((mediaItem) => {
        if (mediaItem.type === 'video') {
          return {
            type: "video" as const,
            sources: [
              {
                src: mediaItem.url,
                type: "video/mp4",
              },
            ],
          };
        }
        return {
          src: hasEnded ? addArchivedClass(mediaItem.url, 1600) : addWidthParam(mediaItem.url, 1600),
        };
      });
    }
    // Fallback to old format
    return item?.images
      ? Object.values(item.images).map((image: any) => ({
          src: hasEnded ? addArchivedClass(image, 1600) : addWidthParam(image, 1600),
        }))
      : [];
  }, [hasMedia, item?.media, item?.images, hasEnded]);

  const boxContainerClass = classNames(
    "w-7/12 h-auto md:w-8/12 xl:w-8/12 self-stretch bg-white flex flex-col items-start justify-start p-1 py-2 pt-3 md:py-4 md:pl-4 md:px-8 box-border md:h-full",
    {
      "!pb-0 md:!pb-8": toggleMobileDiv,
    }
  );

  const timeContainerClass = classNames(
    "block opacity-90 bottom-0 w-full py-0 md:py-2 text-white text-xs bg-blue text-white  z-40",
    {
      "!bg-danger !text-white": isEnding && !hasEnded,
    }
  );

  const imageContainerClass = classNames(
    "relative w-full select-none md:bg-aliceblue"
  );

  const openBidModal = () => {
    if (!bidModal || !loginModal) return;

    if (!user?.id) {
      loginModal.setTitle("Logga in på Tovek.se");
      loginModal.onOpen();
      return;
    }

    setBidModal((prev: any) => ({ ...prev, isOpen: true }));
  };

  const bidModalContent = () => (
    <div className="full-width md:hidden mt-6">
      <div className="container">
        <div className="flex flex-col self-stretch relative font-semibold text-base mb-4 px-6">
          <span className="font-normal">
            {hasEnded
              ? "Slutpris"
              : bidData && bidData?.bids?.length > 0
              ? "Ledande bud"
              : "Utgångspris"}
            :{" "}
          </span>
          <span className="text-2xl">
            <CurrentBid
              auctionItem={item}
              bids={bidData?.bids}
              showLeadingUser
              small
            />
          </span>
        </div>
        <div
          className={classNames(
            "p-6 -mb-8 md:-mb-10 flex flex-col items-start justify-start gap-[24px] bg-aliceblue text-paragraph"
          )}
          ref={bidDivRef}
        >
          <BidCountDown
            item={item}
            hasEnded={hasEnded}
            isWinner={isWinner}
            bidData={bidData}
            serverTime={serverTime}
            isFetching={isFetching}
          />
          <AuctionBidFieldMemomized
            leadingAutoBid={leadingAutoBid}
            hasEnded={hasEnded}
            auctionItem={item}
            leadingBid={leadingBid}
            isLeadingAutoBid={isLeadingAutoBid}
            ref={inputBidFieldRef}
          />
          <div className="bg-aliceblue py-4 w-full">
            <AuctionBidHistory
              auctionItem={item}
              user={user}
              hasEnded={hasEnded}
              bidData={bidData}
              bidIsLoading={false}
              showAllBids={showAllBids}
              setSHowAllBids={setSHowAllBids}
              noMaxHeight
              extraClass="max-h-[300px]"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const imagesStringified = JSON.stringify(largeImages);

  const memoizedLightbox = useMemo(() => {
    return (
      <Lightbox
        className={hasEnded ? "archived-lightbox" : undefined}
        styles={{
          container: { backgroundColor: "rgba(255, 255, 255, .95)" },
          root: {
            "--yarl__color_button": "#2D2D32",
            "--yarl__color_button_disabled": "#dfdfdf",
            "--yarl__color_button_active": "#0a5599",
            "--yarl__thumbnails_thumbnail_border_color": "transparent",
            "--yarl__thumbnails_thumbnail_active_border_color": "#397ab6",
            "--yarl__button_filter": "none",
            "--yarl__thumbnails_container_background_color": "#0a5599",
            "--yarl__thumbnails_thumbnail_background": "transparent",
            "--yarl__color_backdrop": "none",
            "--yarl__slide_icon_loading_color": "#397ab6",
          },
        }}
        plugins={[Zoom, Thumbnails, Video]}
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={largeImages}
      />
    );
  }, [openLightbox, imagesStringified]);

  if (!item?.itemMinBid) {
    return (
      <div className="flex flex-row md:flex-row justify-start bg-white w-full rounded-lg">
        <span className="text-xl md:text-2xl self-stretch relative leading-[1.2] font-semibold text-blue p-5">
          <span className="!no-underline">
            {item.itemSortNo ? `Rop ${item.itemSortNo}: ` : ""}
            <h3 className="text-xl md:text-2xl self-stretch relative inline  leading-[1.2] font-semibold text-blue">
              Utgår
            </h3>
          </span>
        </span>
      </div>
    );
  }

  return (
    <div id={cardId} className={containerClass} ref={cardRef}>

      {memoizedLightbox}
      <ModalElement
        isOpen={bidModal?.isOpen}
        modalTitleClass="!mb-0"
        size="lg"
        modalTitle={itemNameFormatted}
        modalBody={bidModalContent()}
        onClose={() => {
          setBidModal((prev: any) => ({ ...prev, isOpen: false }));

          if (cardRef.current) {
            cardRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }}
      />

      <div className="flex flex-row md:flex-row justify-start bg-white w-full">
        <div className="w-5/12 md:w-4/12 xl:w-4/12 md:max-w-[300px]">
          <div className="h-full relative flex flex-col p-2">
            <div className={imageContainerClass}>
              <div className="absolute top-0 left-0 uppercase text-white text-xss md:text-xs font-bold px-2 py-1 md:px-3 md:py-2 z-[100]" style={{ background: 'rgba(0,0,0,0.45)' }}>
                {formatDate(item.itemEndTime)}
              </div>

              {(photosCount > 0 || videosCount > 0) && (
                <div className="hidden md:flex absolute top-0 right-0 flex-col gap-1.5 p-3 z-[50]">
                  {photosCount > 0 && (
                    <div
                      className="flex items-center gap-1.5 bg-black/50 rounded px-2 py-1 text-white cursor-zoom-in"
                      onClick={() => setOpenLightbox(true)}
                    >
                      <FiCamera className="w-4 h-4" />
                      <span className="text-sm font-semibold">{photosCount}</span>
                    </div>
                  )}
                  {videosCount > 0 && (
                    <div
                      className="flex items-center gap-1.5 bg-black/50 rounded px-2 py-1 text-white cursor-zoom-in"
                      onClick={() => setOpenLightbox(true)}
                    >
                      <FiVideo className="w-4 h-4" />
                      <span className="text-sm font-semibold">{videosCount}</span>
                    </div>
                  )}
                </div>
              )}

              {(firstMediaItem || images?.length) && !hasEnded ? (
                <div
                  onClick={() => setOpenLightbox(true)}
                  className="cursor-zoom-in w-full md:bg-aliceblue"
                >
                  <div className="auction-image-wrapper relative">
                    {firstMediaItem?.type === 'video' ? (
                      <VideoWrapper
                        src={firstMediaItem.url}
                        className="w-full auction-item-image max-h-[250px]"
                      />
                    ) : (
                      <>
                        <Image
                          src={addWidthParam(images[0], 800)}
                          alt={item.itemTitle || "Auktionsföremål"}
                          className="w-full !object-contain md:!object-cover auction-item-image"
                          height={800}
                          width={1200}
                          sizes="(max-width: 768px) 100vw, 800px"
                        />
                        <div className="absolute bottom-0 left-0 right-0 top-0 h-full w-full overflow-hidden bg-[hsl(0,0%,98.4%,0.2)] bg-fixed opacity-0 transition duration-300 ease-in-out hover:opacity-100"></div>
                      </>
                    )}
                    {/* Mobile media overlay - Photos and Videos */}
                    {(photosCount > 0 || videosCount > 0) && (
                      <div
                        className="md:hidden absolute bottom-0 left-0 right-0 w-full bg-black/50 flex items-center justify-center gap-3 px-2 py-1 cursor-zoom-in"
                        onClick={() => setOpenLightbox(true)}
                      >
                        {photosCount > 0 && (
                          <div className="flex items-center gap-1 text-white">
                            <FiCamera className="w-3 h-3" />
                            <span className="text-xs font-semibold">{photosCount}</span>
                          </div>
                        )}
                        {videosCount > 0 && (
                          <div className="flex items-center gap-1 text-white">
                            <FiVideo className="w-3 h-3" />
                            <span className="text-xs font-semibold">{videosCount}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : hasEnded ? (
                <div
                  onClick={() => setOpenLightbox(true)}
                  className="cursor-zoom-in h-full w-full"
                >
                  <div className="auction-image-wrapper relative">
                    <AuctionEndedImage
                      image={images?.[0] || ""}
                      text={hasEnded ? "" : "Ingen bild hittades"}
                    />{" "}
                    {(photosCount > 0 || videosCount > 0) && (
                      <div
                        className="md:hidden absolute bottom-0 left-0 right-0 w-full bg-black/50 flex items-center justify-center gap-3 px-2 py-1 cursor-zoom-in"
                        onClick={() => setOpenLightbox(true)}
                      >
                        {photosCount > 0 && (
                          <div className="flex items-center gap-1 text-white">
                            <FiCamera className="w-3 h-3" />
                            <span className="text-xs font-semibold">{photosCount}</span>
                          </div>
                        )}
                        {videosCount > 0 && (
                          <div className="flex items-center gap-1 text-white">
                            <FiVideo className="w-3 h-3" />
                            <span className="text-xs font-semibold">{videosCount}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="cursor-zoom-in h-full w-full bg-blue">
                  <div className="auction-image-wrapper relative">
                    <NoImageFound
                      containerClass="auction-item-image"
                      text={hasEnded ? "" : "Ingen bild hittades"}
                    />{" "}
                    {(photosCount > 0 || videosCount > 0) && (
                      <div className="md:hidden absolute bottom-0 left-0 right-0 w-full bg-black/50 flex items-center justify-center gap-3 px-2 py-1">
                        {photosCount > 0 && (
                          <div className="flex items-center gap-1 text-white">
                            <FiCamera className="w-3 h-3" />
                            <span className="text-xs font-semibold">{photosCount}</span>
                          </div>
                        )}
                        {videosCount > 0 && (
                          <div className="flex items-center gap-1 text-white">
                            <FiVideo className="w-3 h-3" />
                            <span className="text-xs font-semibold">{videosCount}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className={timeContainerClass}>
              <div className="px-2 md:px-4">
                <BidCountDown
                  isFetching={isFetching}
                  item={item}
                  hasEnded={hasEnded}
                  isWinner={isWinner}
                  bidData={bidData}
                  serverTime={serverTime}
                  noCountDownTextColor
                />{" "}
              </div>
            </div>
            <div className="relative block -mt-2 sm:hidden leading-1 text-xss flex space-y-1 flex-col rounded-xl w-full md:w-fit h-fit md:my-0 pt-4">
              <div className="self-stretch relative leading-1">
                <span className="font-semibold">Moms:</span> {item?.itemVatValue && item?.itemVatValue >= 1 ? `${item.itemVatValue}% tillkommer` : `${item?.itemVatValue}%`}
              </div>
              <div className="self-stretch relative leading-1">
                <span className="font-semibold">Slagavgift:</span>{" "}
                {formatPrice(item.itemFeeValue)} exkl. moms
              </div>
            </div>
          </div>
        </div>
        <div className={boxContainerClass}>
          <div className="self-stretch flex flex-col items-start justify-start break-words">
            <div className="flex flex-row items-center">
              <FavoriteButtonMemomized
                auctionItemFavorite={auctionItemFavorite}
                extraClassName="!top-0 !right-0 !m-0 !-mt-1 !pl-0 !relative"
              />
              <span className="text-xl md:text-2xl self-stretch relative leading-[1.2] font-semibold text-blue">
                <Link href={itemLink || "#"} className="!no-underline" prefetch={false}>
                  {item.itemSortNo ? `Rop ${item.itemSortNo}: ` : ""}
                  <h3 className="text-xl md:text-2xl self-stretch relative inline  leading-[1.2] font-semibold text-blue">
                    {item.itemTitle}
                  </h3>
                </Link>
              </span>
            </div>
            {item?.address?.addressTitle && (
              <div className="self-stretch relative leading-1 font-normal text-paragraph text-14 md:text-1xl mt-2 flex flex-row space-x-2 items-center">
                <span className="text-muted-bg">
                  <MarkerSvg />
                </span>
                <span>{item?.address?.addressTitle}</span>
              </div>
            )}
          </div>
          <div className="flex flex-col xl:space-y-0 xl:space-x-10 md:justify-between xl:flex-row w-full items-start">
            <div className="flex flex-col space-y-8 w-full flex-grow md:h-full">
              <div className="self-stretch relative text-sm text-black md:h-full">
                <div className="flex-1 flex flex-col items-start justify-start h-full">
                  <div className="self-stretch flex flex-col items-start justify-start gap-[4px] md:space-y-6 md:h-full">
                    <div className="flex flex-col self-stretch relative leading-1 font-semibold text-base mt-4 md:mt-6 mb-2 md:mt-8 md:mb-4">
                      <span className="font-normal md:font-semibold text-xs md:text-14 mb-1 md:mb-0">
                        {hasEnded
                          ? "Slutpris"
                          : bidData && bidData?.bids?.length > 0
                          ? "Ledande bud"
                          : "Utgångspris"}
                        :{" "}
                      </span>
                      <span className="text-xl md:text-3xl">
                        <CurrentBid
                          auctionItem={item}
                          bids={bidData?.bids}
                          showLeadingUser
                        />
                      </span>
                    </div>

                    <div className="flex flex-row sm:flex-row gap-3 md:hidden mt-1 md:mt-0 md:mb-6 md:mb-0 w-full">
                      {!hasEnded && (
                        <Button
                          onClick={openBidModal}
                          className="h-full flex-grow text-xss !text-white md:text-base px-3 py-2 md:px-2"
                          textCenter
                        >
                          Lägg bud
                        </Button>
                      )}
                      <Link
                        href={itemLink || "#"}
                        className="block !no-underline flex-grow"
                        prefetch={false}
                      >
                        <Button
                          bgColor="white"
                          textColor="blue"
                          variant="outlined"
                          borderColor="blue"
                          className="hover:bg-blue w-full hover:text-white !text-blue text-xss px-3 py-2 md:px-2 !hover:bg-opacity-1 md:text-base px-4 md:px-2"
                          textCenter
                        >
                          Se mer info
                        </Button>
                      </Link>
                    </div>
                    <div className="flex flex-row justify-between w-full">
                      <div className="relative hidden sm:mt-4 sm:block mt-auto leading-1 text-xs flex space-y-2 flex-col rounded-xl w-full md:w-fit h-fit !mt-auto my-6 mb-0 md:my-0">
                        <div className="self-stretch relative leading-1">
                          <span className="font-semibold">Moms:</span>{" "}
                          {item?.itemVatValue && item?.itemVatValue >= 1 ? `${item.itemVatValue}% tillkommer` : `${item?.itemVatValue}%`}
                        </div>
                        <div className="self-stretch relative leading-1">
                          <span className="font-semibold">Slagavgift:</span>{" "}
                          {formatPrice(item.itemFeeValue)} exkl. moms
                        </div>
                      </div>
                      <Link
                        href={itemLink || "#"}
                        className="block !no-underline w-fit hidden md:block"
                        prefetch={false}
                      >
                        <Button
                          bgColor="white"
                          textColor="blue"
                          variant="outlined"
                          borderColor="blue"
                          className="hover:bg-blue w-full hover:!text-white !text-blue text-xss px-4 !py-0 md:px-2 !hover:bg-opacity-1 md:text-base px-4 md:px-2"
                          textCenter
                        >
                          Se mer info
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="hidden md:block w-full md:w-4/12 xl:w-4/12 rounded-tl-none rounded-tr-lg rounded-br-lg rounded-bl-none bg-aliceblue md:flex flex-col items-start justify-start p-6 box-border gap-3">
          <div className="mb-6 md:mb-0">
            <BidCountDown
              isFetching={isFetching}
              item={item}
              hasEnded={hasEnded}
              isWinner={isWinner}
              bidData={bidData}
              serverTime={serverTime}
            />
          </div>

          <AuctionBidFieldMemomized
            leadingAutoBid={leadingAutoBid}
            hasEnded={hasEnded}
            auctionItem={item}
            leadingBid={leadingBid}
            isLeadingAutoBid={isLeadingAutoBid}
            ref={inputBidFieldRef}
          />
          <div className="mt-6 md:mt-4 w-full">
            <AuctionBidHistory
              auctionItem={item}
              user={user}
              hasEnded={hasEnded}
              bidData={bidData}
              showAllBids={showAllBids}
              setSHowAllBids={setSHowAllBids}
              bidIsLoading={false}
              noMaxHeight
              extraClass="max-h-[300px]"
              showMorePosition="title"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GridViewItem;